import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Divider,
  Center,
  Loader,
} from "@mantine/core";
import { notifySuccess } from "../../../../../utils/notify";
import {
  IconAlertCircle,
  IconCheck,
  IconSend,
  IconPrinter,
} from "@tabler/icons-react";
import { useApp } from "../../../../../context/AppContext";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { useSalon } from "../../../../../context/SalonContext";
import { SalonContextType } from "../../../../../types/context";
import { ParsedPhoneNumber } from "../../../../common/StandardizedPhoneInput";
import GiftCardRecipientFields from "../../../../common/GiftCardRecipientFields";
import GiftCardCertificate from "../../../../common/GiftCardCertificate";
import useGiftCardPrint from "../../../../common/useGiftCardPrint";
import {
  GiftCardSummary,
  GiftCardDeliveryChannel,
} from "../../../../../types/giftCards";
import { giftCardRecipientName } from "../../../../../utils/giftCardRecipient";

interface GiftCardPostCheckoutModalProps {
  opened: boolean;
  /** Finalize — the sale is complete; close and return to the calendar. */
  onClose: () => void;
  /** Salon UUID (businessInfo.id). */
  salonId: string;
  /** The paid gift-card sale transaction's UUID, used to load the minted card. */
  transactionUuid: string;
  /** Purchaser (buyer) display name, shown as "From" on the certificate. */
  purchaserName?: string | null;
}

// A minimal email check — the backend re-validates; this just stops staff
// submitting an obviously-empty/garbled address.
const _EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// The mint runs in the payment-completion path; for a synchronous (cash)
// checkout it's ready immediately, for a card webhook it can lag a beat — so
// retry a few times before falling back.
const _MAX_LOAD_ATTEMPTS = 4;
const _LOAD_RETRY_MS = 1500;

// Past-tense verb for the channel the recipient was auto-notified on.
const _channelVerb = (channel: GiftCardDeliveryChannel): string =>
  channel === "both"
    ? "texted and emailed"
    : channel === "email"
      ? "emailed"
      : "texted";

/**
 * Post-checkout follow-up shown after a gift-card sale is paid.
 *
 * If a recipient was captured at sale time, the card was already auto-notified on
 * payment — so we just confirm "sent to {recipient}" and finish (the recipient
 * self-claims via the link; staff can attach it to a client later from the
 * client's Credits tab). Only when NO recipient was captured (an in-person or
 * line-item sale, `delivery_channel` null) do we offer the send-link fallback so
 * the code can still reach a recipient.
 */
export default function GiftCardPostCheckoutModal({
  opened,
  onClose,
  salonId,
  transactionUuid,
  purchaserName,
}: GiftCardPostCheckoutModalProps) {
  const { isMobile } = useResponsive();
  const { getTransactionGiftCards, sendGiftCardLink } = useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const { printCard, certificate } = useGiftCardPrint();

  const [card, setCard] = useState<GiftCardSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Send-link fallback sub-form (only used when no recipient was captured).
  const [sendChannel, setSendChannel] =
    useState<GiftCardDeliveryChannel>("sms");
  const [recipientFirstName, setRecipientFirstName] = useState("");
  const [recipientLastName, setRecipientLastName] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [parsedPhone, setParsedPhone] = useState<ParsedPhoneNumber | null>(
    null,
  );
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientMessage, setRecipientMessage] = useState("");

  // Load the minted card when the modal opens, retrying to absorb webhook lag.
  useEffect(() => {
    if (!opened) return;
    setCard(null);
    setIsLoading(true);
    setLoadError(null);
    setError(null);
    setIsSubmitting(false);
    setSendChannel("sms");
    setRecipientFirstName("");
    setRecipientLastName("");
    setPhoneDisplay("");
    setParsedPhone(null);
    setRecipientEmail("");
    setRecipientMessage("");

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (n: number) => {
      try {
        const cards = await getTransactionGiftCards(salonId, transactionUuid);
        if (cancelled) return;
        if (cards.length > 0) {
          setCard(cards[0]);
          setIsLoading(false);
          return;
        }
        if (n < _MAX_LOAD_ATTEMPTS) {
          timer = setTimeout(() => attempt(n + 1), _LOAD_RETRY_MS);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load minted gift card:", err);
        setLoadError(
          err instanceof Error
            ? err.message
            : "Could not load the gift card. You can attach it later by its code.",
        );
        setIsLoading(false);
      }
    };

    attempt(1);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [opened, salonId, transactionUuid, getTransactionGiftCards]);

  const wantsSms = sendChannel === "sms" || sendChannel === "both";
  const wantsEmail = sendChannel === "email" || sendChannel === "both";
  const emailValid = _EMAIL_RE.test(recipientEmail.trim());
  // Ready once every field the chosen channel needs is filled: a phone for
  // sms/both, a valid email for email/both.
  const sendReady =
    (!wantsSms || parsedPhone !== null) && (!wantsEmail || emailValid);

  const handleSend = useCallback(async () => {
    if (!card || !sendReady) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await sendGiftCardLink(salonId, card.id, {
        recipient_first_name: recipientFirstName.trim() || undefined,
        recipient_last_name: recipientLastName.trim() || undefined,
        recipient_area_code: wantsSms ? parsedPhone?.areaCode : undefined,
        recipient_phone_number: wantsSms ? parsedPhone?.phoneNumber : undefined,
        recipient_email: wantsEmail ? recipientEmail.trim() : undefined,
        delivery_channel: sendChannel,
        gift_message: recipientMessage.trim() || undefined,
      });
      // The request can succeed while a channel silently didn't send (e.g. the
      // customer-email queue isn't wired yet). On an email-only send that means
      // nothing reached the recipient — surface it instead of implying success.
      if (wantsEmail && !wantsSms && response.email_sent === false) {
        setError(
          "Saved the recipient, but the email couldn't be sent — check the " +
            "gift-card email setup, or send by text instead.",
        );
        return;
      }
      notifySuccess(
        wantsSms && wantsEmail
          ? "We sent the recipient their claim link by text and email."
          : wantsEmail
            ? "We emailed the recipient their claim link."
            : "We texted the recipient their claim link.",
        { title: "Redemption link sent" },
      );
      onClose();
    } catch (err) {
      console.error("Failed to send gift card link:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to send the link. Please check the recipient details and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    card,
    sendReady,
    sendChannel,
    wantsSms,
    wantsEmail,
    parsedPhone,
    recipientEmail,
    recipientMessage,
    sendGiftCardLink,
    salonId,
    recipientFirstName,
    recipientLastName,
    onClose,
  ]);

  // The certificate fields shared by the on-screen preview and the printout.
  const certData = card
    ? {
        code: card.code,
        initialAmount: card.initial_amount,
        balance: card.balance,
        status: card.status,
        recipientName: giftCardRecipientName(
          card.recipient_first_name,
          card.recipient_last_name,
        ),
        purchaserName,
        giftMessage: card.gift_message,
        expiresAt: card.expires_at,
        purchasedAt: card.purchased_dt,
      }
    : null;

  const renderCertificate = () =>
    certData && (
      <GiftCardCertificate
        variant="screen"
        businessName={businessInfo?.salon_name || ""}
        businessLogoUrl={businessInfo?.logo_url}
        {...certData}
      />
    );

  const handlePrint = () => {
    if (certData) printCard(certData);
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <Loader color="var(--brand-teal)" />
            <Text size="sm" c="dimmed">
              Finalizing the gift card…
            </Text>
          </Stack>
        </Center>
      );
    }

    // Mint not visible yet (or errored): the sale still succeeded, so let staff
    // finish and attach later from the client's Credits tab by code.
    if (!card) {
      return (
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            {loadError ??
              "The gift card is being finalized. You can attach it to a client later from their Credits tab using the card's code."}
          </Alert>
          <Group justify="flex-end">
            <Button color="var(--brand-teal)" onClick={onClose}>
              Done
            </Button>
          </Group>
        </Stack>
      );
    }

    // A recipient was captured at sale time ⇒ the card was auto-notified on
    // payment. Confirm and finish — no redundant send step.
    if (card.delivery_channel) {
      const verb = _channelVerb(card.delivery_channel);
      const who = card.recipient_first_name?.trim() || "the recipient";
      return (
        <Stack gap="md">
          {renderCertificate()}
          <Alert icon={<IconCheck size={16} />} color="teal" variant="light">
            {`We ${verb} ${who} their claim link with the code.`}
          </Alert>
          <Text size="sm" c="dimmed">
            Print a copy to hand over, or you're done — {who} can claim it onto
            their own profile from the link. You can also attach it to a client
            anytime from their Credits tab.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={handlePrint}
              leftSection={<IconPrinter size={18} />}
            >
              Print gift card
            </Button>
            <Button color="var(--brand-teal)" onClick={onClose}>
              Done
            </Button>
          </Group>
        </Stack>
      );
    }

    // No recipient captured (in-person / line-item sale): offer the send-link
    // fallback so the code can still reach a recipient — or skip.
    return (
      <Stack gap="md">
        {renderCertificate()}
        <Group justify="center">
          <Button
            variant="default"
            size="xs"
            onClick={handlePrint}
            leftSection={<IconPrinter size={16} />}
          >
            Print gift card
          </Button>
        </Group>
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}
        <Text size="sm" c="dimmed">
          This gift card wasn't sent to anyone at the sale. Print it to hand
          over in person, or text/email the recipient a link to claim it onto
          their own profile.
        </Text>
        <GiftCardRecipientFields
          channel={sendChannel}
          onChannelChange={setSendChannel}
          firstName={recipientFirstName}
          onFirstNameChange={setRecipientFirstName}
          lastName={recipientLastName}
          onLastNameChange={setRecipientLastName}
          phoneDisplay={phoneDisplay}
          onPhoneDisplayChange={setPhoneDisplay}
          parsedPhone={parsedPhone}
          onParsedPhoneChange={setParsedPhone}
          email={recipientEmail}
          onEmailChange={setRecipientEmail}
          message={recipientMessage}
          onMessageChange={setRecipientMessage}
          emailValid={emailValid}
          disabled={isSubmitting}
        />
        <Divider />
        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Skip — finish sale
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSend}
            loading={isSubmitting}
            disabled={!sendReady || isSubmitting}
            leftSection={<IconSend size={18} />}
          >
            Send the Link
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Gift Card Sold</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      // The printable certificate must not be dismissed by an accidental click
      // outside (or Escape) — staff close it deliberately via the X or Done.
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      {renderBody()}
      {certificate}
    </Modal>
  );
}
